/**
 * Modal "Registrar retirada" — a pessoa que leva as peças/itens na
 * expedição assina na tela. Grava quem retirou (nome, documento, telefone),
 * o que levou (itens e quantidades — retirada parcial permitida, com saldo
 * calculado no backend), quem entregou (usuário logado) e quando; foto
 * opcional. A retirada completa vira o marco "Expedida".
 *
 * Pensado para tablet/celular do almoxarife: campos grandes, canvas de
 * assinatura com o dedo, comprovante para imprimir logo depois.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiCamera, FiRotateCcw, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { listarRetiradas, registrarRetirada } from "../../../services/solicitacoes";
import { fmtQtd } from "../helpers";
import { Carregando } from "../_shared/Estados";
import AssinaturaCanvas from "./AssinaturaCanvas";
import { imprimirComprovanteRetirada } from "./imprimirRetirada";
import {
  Checkbox,
  ErrorMsg,
  Field,
  GhostBtn,
  InlineError,
  Input,
  Label,
  NeutralBanner,
  RowEnd,
  TextArea,
} from "./styles";

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Bloco = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const Titulo = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-2);
`;

const ItemRow = styled.label`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 96px;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  font-size: 13px;
  cursor: pointer;
  .desc { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .saldo { font-size: 11px; color: var(--ink-3); }
  &[data-esgotado="true"] { opacity: 0.55; cursor: default; }
`;

const QtdInput = styled(Input)`
  height: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const FotoPreview = styled.img`
  display: block;
  max-height: 160px;
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid var(--border);
`;

const MAX_FOTO_PX = 1280;

/** Reduz a foto para caber em base64 sem estourar a request (JPEG). */
function redimensionarImagem(file, max = MAX_FOTO_PX) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const escala = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * escala);
      c.height = Math.round(img.height * escala);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Imagem inválida.")); };
    img.src = url;
  });
}

function numero(v) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export default function RetiradaModal({ familia, id, numero: numeroSol, detalhe, onClose, onDone }) {
  const { request } = useAuth();
  const toast = useToast();
  const assinaturaRef = useRef(null);

  const [saldo, setSaldo] = useState(null);
  const [anteriores, setAnteriores] = useState([]);
  const [erro, setErro] = useState("");
  const [erroEnvio, setErroEnvio] = useState("");
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [observacao, setObservacao] = useState("");
  const [foto, setFoto] = useState("");
  const [assinaturaVazia, setAssinaturaVazia] = useState(true);
  const [imprimir, setImprimir] = useState(true);
  // { [item_id]: { marcado, quantidade } }
  const [sel, setSel] = useState({});

  useEffect(() => {
    let vivo = true;
    listarRetiradas(request, familia, id)
      .then((r) => {
        if (!vivo) return;
        const linhas = r?.saldo || [];
        setSaldo(linhas);
        setAnteriores(r?.retiradas || []);
        const inicial = {};
        linhas.forEach((l) => {
          const falta = numero(l.falta);
          inicial[l.item_id] = { marcado: falta > 0, quantidade: falta > 0 ? l.falta : "0" };
        });
        setSel(inicial);
      })
      .catch((e) => vivo && setErro(e?.message || "Erro ao carregar os itens."));
    return () => { vivo = false; };
  }, [request, familia, id]);

  const marcados = useMemo(
    () => (saldo || []).filter((l) => sel[l.item_id]?.marcado && numero(sel[l.item_id]?.quantidade) > 0),
    [saldo, sel],
  );
  const tudoSai = useMemo(
    () => (saldo || []).every((l) => numero(l.falta) === 0 || (
      sel[l.item_id]?.marcado && numero(sel[l.item_id]?.quantidade) >= numero(l.falta)
    )),
    [saldo, sel],
  );

  const alternar = (l) => {
    if (numero(l.falta) === 0) return;
    setErroEnvio("");
    setSel((s) => ({ ...s, [l.item_id]: { ...s[l.item_id], marcado: !s[l.item_id]?.marcado } }));
  };
  const mudarQtd = (l, v) => {
    setErroEnvio("");
    setSel((s) => ({ ...s, [l.item_id]: { marcado: numero(v) > 0, quantidade: v } }));
  };

  const escolherFoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      setFoto(await redimensionarImagem(f));
    } catch (err) {
      toast.error(err.message || "Não foi possível ler a foto.");
    }
  };

  const confirmar = async () => {
    setErroEnvio("");
    const nomeLimpo = nome.trim();
    if (nomeLimpo.length < 3) {
      setErroEnvio("Informe o nome completo de quem está retirando.");
      throw new Error("nome");
    }
    if (marcados.length === 0) {
      setErroEnvio("Marque ao menos um item com quantidade maior que zero.");
      throw new Error("itens");
    }
    const excesso = marcados.find((l) => numero(sel[l.item_id].quantidade) > numero(l.falta) + 1e-9);
    if (excesso) {
      setErroEnvio(`"${excesso.descricao}": quantidade maior que o saldo a retirar (${fmtQtd(excesso.falta)}).`);
      throw new Error("qtd");
    }
    const assinatura = assinaturaRef.current?.toDataURL() || "";
    if (!assinatura) {
      setErroEnvio("A assinatura de quem retira é obrigatória.");
      throw new Error("assinatura");
    }
    const body = {
      retirado_por_nome: nomeLimpo,
      retirado_por_documento: documento.trim(),
      retirado_por_telefone: telefone.trim(),
      assinatura,
      foto,
      observacao: observacao.trim(),
      itens: marcados.map((l) => ({
        item_id: l.item_id, quantidade: String(sel[l.item_id].quantidade).replace(",", "."),
      })),
    };
    let resp;
    try {
      resp = await registrarRetirada(request, familia, id, body);
    } catch (e) {
      setErroEnvio(e?.message || "Não foi possível registrar a retirada.");
      throw e;
    }
    if (imprimir && resp?.retirada) {
      const ok = imprimirComprovanteRetirada(
        { ...(detalhe || {}), ...resp, numero: resp.numero || numeroSol },
        { ...resp.retirada, assinatura, foto },
      );
      if (!ok) toast.error("Comprovante não abriu (pop-up bloqueado?). Reimprima pelo histórico de retiradas.");
    }
    onDone?.(resp);
  };

  const footer = saldo && (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {erroEnvio && <InlineError style={{ fontSize: 12.5 }}>{erroEnvio}</InlineError>}
      <RowEnd style={{ alignItems: "center" }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, marginRight: "auto" }}>
          <Checkbox checked={imprimir} onChange={(e) => setImprimir(e.target.checked)} />
          Imprimir comprovante
        </label>
        <GhostBtn type="button" onClick={onClose}>Voltar</GhostBtn>
        <AsyncButton variant="primary" silentError
                     successMessage={tudoSai ? "Retirada registrada — solicitação expedida." : "Retirada parcial registrada."}
                     onClick={confirmar} disabled={assinaturaVazia || marcados.length === 0}>
          {tudoSai ? "Confirmar retirada" : "Confirmar retirada parcial"}
        </AsyncButton>
      </RowEnd>
    </div>
  );

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={`Retirada · ${numeroSol || ""}`}
      subtitle="Quem leva assina aqui. O registro vale como expedição."
      width="min(96vw, max(52vw, 600px))"
      footer={footer}
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {!erro && !saldo && <Carregando />}
      {saldo && (
        <>
          {anteriores.length > 0 && (
            <NeutralBanner>
              Já houve {anteriores.length} retirada(s) nesta solicitação — os itens abaixo mostram só o saldo.
            </NeutralBanner>
          )}

          <Bloco>
            <Titulo>Quem está retirando</Titulo>
            <Grade>
              <Field style={{ gridColumn: "1 / -1" }}>
                <Label>Nome completo *</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)}
                       placeholder="Ex.: João da Silva (montador)" autoFocus
                       style={{ height: 44, fontSize: 15 }} />
              </Field>
              <Field>
                <Label>Documento (CPF/RG)</Label>
                <Input value={documento} onChange={(e) => setDocumento(e.target.value)} inputMode="numeric" />
              </Field>
              <Field>
                <Label>Telefone</Label>
                <Input value={telefone} onChange={(e) => setTelefone(e.target.value)} inputMode="tel" />
              </Field>
            </Grade>
          </Bloco>

          <Bloco>
            <Titulo>O que está saindo</Titulo>
            {saldo.length === 0 && <NeutralBanner>Esta solicitação não tem itens discriminados.</NeutralBanner>}
            {saldo.map((l) => {
              const esgotado = numero(l.falta) === 0;
              const s = sel[l.item_id] || {};
              return (
                <ItemRow key={l.item_id} data-esgotado={esgotado}>
                  <Checkbox checked={!!s.marcado} disabled={esgotado} onChange={() => alternar(l)} />
                  <div className="desc" title={l.descricao}>
                    {l.descricao || "Item"}
                    <div className="saldo">
                      {esgotado
                        ? `já retirado (${fmtQtd(l.quantidade)})`
                        : `saldo ${fmtQtd(l.falta)} de ${fmtQtd(l.quantidade)}`}
                    </div>
                  </div>
                  <QtdInput type="text" inputMode="decimal" value={s.quantidade ?? ""}
                            disabled={esgotado}
                            onChange={(e) => mudarQtd(l, e.target.value)} />
                </ItemRow>
              );
            })}
          </Bloco>

          <Bloco>
            <Titulo>Assinatura *</Titulo>
            <AssinaturaCanvas ref={assinaturaRef} onChange={setAssinaturaVazia} />
            <RowEnd>
              <GhostBtn type="button" onClick={() => assinaturaRef.current?.limpar()} disabled={assinaturaVazia}>
                <FiRotateCcw size={14} /> Limpar assinatura
              </GhostBtn>
            </RowEnd>
          </Bloco>

          <Bloco>
            <Titulo>Foto (opcional)</Titulo>
            {foto ? (
              <>
                <FotoPreview src={foto} alt="Foto da retirada" />
                <RowEnd>
                  <GhostBtn type="button" onClick={() => setFoto("")}><FiTrash2 size={14} /> Remover foto</GhostBtn>
                </RowEnd>
              </>
            ) : (
              <GhostBtn as="label" style={{ alignSelf: "flex-start", cursor: "pointer" }}>
                <FiCamera size={14} /> Tirar ou escolher foto
                <input type="file" accept="image/*" onChange={escolherFoto} style={{ display: "none" }} />
              </GhostBtn>
            )}
          </Bloco>

          <Bloco>
            <Field>
              <Label>Observação</Label>
              <TextArea value={observacao} onChange={(e) => setObservacao(e.target.value)}
                        placeholder="Ex.: levou só as portas; o restante sai amanhã" />
            </Field>
          </Bloco>
        </>
      )}
    </SlideOverPanel>
  );
}
